import * as React from 'react';
// Import props.
import {
	ComparativeMortgageesSummaryProps,
	FormPopupConfirmationProps,
	FormMortgageesComparisonProps,
	FormProps,
	ScrollbarProps,
	SubformMortgageesComparisonProps,
} from '@app/props';
// Import hooks.
import {
	useInputButton,
} from '@app/hooks/view';
// Import mui components.
import {
	Add,
} from '@mui/icons-material';
// Import enumerations.
import {
	AppThemeColor,
    ButtonSize,
} from '@app/enumerations';

interface UseFormMortgageesComparisonParameters {
	id: string;
	props: FormMortgageesComparisonProps,
}

interface UseFormMortgageesComparisonResults {
	applyFormProps: () => FormProps & ScrollbarProps;
	applySubformMortgageesComparisonProps: () => SubformMortgageesComparisonProps
}

const useFormMortgageesComparison = (params: UseFormMortgageesComparisonParameters): UseFormMortgageesComparisonResults => {

	// #region exported methods

	/**
	 * Returns props for form. 
	 **/
	const applyFormProps = (): FormProps & ScrollbarProps => {
		return {
			id: generateIdForForm(),
		};
	};

	const applySubformMortgageesComparisonProps = (): SubformMortgageesComparisonProps => {
		return {
			id: generateIdForMortgageesComparisonSubform(),
			comparativeMortgageesSummaryProps: { ...applyComparativeMortgageesSummaryProps() },
			removeMortgageeFormPopupConfirmationProps: { ...applyRemoveMortgageeFormPopupConfirmationProps() },
			addMortgageeButtonProps: addMortgageeButtonInput.applyProps(),
			isMortgageeListEmpty: isMortgageeListEmpty,
		};
	}

	// #endregion exported methods


	// #region private methods

	// #region apply-props.

	/**
	 * Returns props for the comparative mortgagees summary. 
	 **/
	const applyComparativeMortgageesSummaryProps = (): ComparativeMortgageesSummaryProps => {
		return {
			id: generateIdForComparativeMortgageesSummary(),
			possibleStates: params.props.possibleStates,
			comparisonMortgageesSummary: params.props.comparisonMortgageesSummary,
			focusToAnyErrorOnMount,
			focusToTopOnMount,
			areHeadersSticky: params.props.areHeadersSticky,
			entityActionProps: {
				...params.props.entityActionProps,
				onRemoveEntity: removeMortgagee,
			}
		};
	};

	/**
	 * Returns props for the remove mortgagee confirmation popup form. 
	 **/
	const applyRemoveMortgageeFormPopupConfirmationProps = (): FormPopupConfirmationProps => {
		return {
			isOpen: openRemoveMortgageeConfirmationPopup,
			onClose: cancelConfirmRemoveMortgagee,
			onConfirm: confirmRemoveMortgagee,
			message: 'Are you sure you want to remove this mortgagee?',
		};
	};

	// #endregion apply-props.

	// #region add mortgagee

	const addMortgagee = (): void => {
		params.props.entityActionProps.onAddEntity();
	}

	// #endregion add mortgagee

	// #region delete mortgagee

	/**
	 *  Starts the remove mortgagee process.
	 **/
	const removeMortgagee = (mortgageeGuid: string): void => {
		setOpenRemoveMortgageeConfirmationPopup(true);
		setMortgageeGuidToBeRemoved(mortgageeGuid);
	}

	/**
	 * Confirms mortgagee remove if user chooses to.
	 **/
	const confirmRemoveMortgagee = (): void => {
		const mortgageeGuid: string | undefined = mortgageeGuidToBeRemoved;
		if (mortgageeGuid !== undefined) {
			setOpenRemoveMortgageeConfirmationPopup(false);
			setMortgageeGuidToBeRemoved(undefined);
			params.props.entityActionProps.onRemoveEntity(mortgageeGuid);
		}
	}

	/**
	 * Cancels the confirmation prompt for removing a mortgagee. 
	 **/
	const cancelConfirmRemoveMortgagee = (): void => {
		setOpenRemoveMortgageeConfirmationPopup(false);
		setMortgageeGuidToBeRemoved(undefined);
	}

	// #endregion delete mortgagee

	// #region id generators

	/**
	 * Generates id for the form. 
	 **/
	const generateIdForForm = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the mortgagees comparison subform. 
	 **/
	const generateIdForMortgageesComparisonSubform = (): string => {
		return `${generateIdForForm()}-mortgageeComparisonSubform`;
	}

	/**
	 * Generates id for the comparative mortgagees summary. 
	 **/
	const generateIdForComparativeMortgageesSummary = (): string => {
		return `${generateIdForForm()}-mortgageesSummary`;
	}


	/**
	 * Generates id for the add mortgagee button input. 
	 **/
	const generateIdForAddMortgageeButtonInput = (): string => {
		return `${generateIdForForm()}-addMortgageeButtonInput`;
	}

	// #endregion id generators

	// #endregion private methods


	// #region variables

	const isMortgageeListEmpty: boolean = React.useMemo(() => params.props.comparisonMortgageesSummary.comparisonEntitySummaries.length == 0, [params.props.comparisonMortgageesSummary.comparisonEntitySummaries]);
	const doAnyMortgageeRequireReview: boolean = React.useMemo(() => {
		return params.props.comparisonMortgageesSummary.comparisonEntitySummaries.some(comparisonMortgageeSummary => {
			return comparisonMortgageeSummary.requiresReview;
		});
	}, [params.props.comparisonMortgageesSummary.comparisonEntitySummaries]);

	const focusToAnyErrorOnMount: boolean = React.useMemo(() => {
		return doAnyMortgageeRequireReview;
	}, [doAnyMortgageeRequireReview]);

	const focusToTopOnMount: boolean = React.useMemo(() => {
		return params.props.focusToTopOnMountIfReviewNotRequired && !doAnyMortgageeRequireReview;
	}, [doAnyMortgageeRequireReview, params.props.focusToTopOnMountIfReviewNotRequired]);

	const [mortgageeGuidToBeRemoved, setMortgageeGuidToBeRemoved] = React.useState<string | undefined>(undefined);
	const [openRemoveMortgageeConfirmationPopup, setOpenRemoveMortgageeConfirmationPopup] = React.useState<boolean>(false);

	const addMortgageeButtonInput = useInputButton({
		id: generateIdForAddMortgageeButtonInput(),
		buttonType: AppThemeColor.PRIMARY,
		label: 'Add Mortgagee',
		size: ButtonSize.SMALL,
		disabled: !isMortgageeListEmpty,
		endAdornmentIcon: Add,
		onClick: addMortgagee,
	});

	// #endregion variables

	// #region life-cycle methods

	// #endregion life-cycle methods

	return {
		applyFormProps,
		applySubformMortgageesComparisonProps,
	};
}

// Export parameters.
export {
	UseFormMortgageesComparisonParameters,
}

// Export results.
export {
	UseFormMortgageesComparisonResults,
}

export default useFormMortgageesComparison;